import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ExternalLink } from 'lucide-react';

const WorkPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = ['all', 'city', 'sport', 'music', 'entertainment'];

  const projects = [
    // City FIRST
    {
      id: 1,
      title: 'Brandsmiths',
      subtitle: 'Award-Winning Law Firm',
      category: 'city',
      services: ['Website', 'Video', 'Social', 'Marketing'],
      image: '/images/clients/Brandsmiths Background - City.jpg',
      logo: '/images/clients/Brandsmiths Logo.png',
      logoSize: 'xlarge',
      link: 'https://brandsmiths.co.uk/',
      gradient: 'from-[#E91E63]/40 via-transparent to-transparent', // Pink gradient bottom to top
      gradientDirection: 'bg-gradient-to-t',
    },
    {
      id: 2,
      title: 'NicheCom',
      subtitle: 'Visual Property Marketing',
      category: 'city',
      services: ['Website', 'Branding', 'Marketing'],
      image: '/images/clients/NicheCom.png',
      logo: '/images/clients/Nichecom-Logo-White.png',
      gradient: 'from-[#4CAF50]/60 via-transparent to-transparent', // Green gradient top to bottom
      gradientDirection: 'bg-gradient-to-b',
    },
    {
      id: 3,
      title: 'VenCap',
      subtitle: 'Investment Management',
      category: 'city',
      services: ['Development'],
      image: '/images/clients/Vencap background - City.png',
      logo: '/images/clients/VenCap Logo.png',
    },
    {
      id: 4,
      title: 'Hagen Wolf',
      subtitle: 'Award-Winning Law Firm',
      category: 'city',
      services: ['Website', 'Marketing'],
      image: '/images/clients/HAgen Wolf Background - City.png',
      logo: '/images/clients/HAgen Wolf Logo.png',
      link: 'https://hagenwolf.co.uk/',
    },
    {
      id: 5,
      title: 'KKV',
      subtitle: 'Investment Fund',
      category: 'city',
      services: ['Website', 'Press', 'Marketing'],
      image: '/images/clients/obgImage-1551195.jpg',
      logo: '/images/clients/KKV Logo.png',
    },
    {
      id: 6,
      title: 'LTS Group',
      subtitle: 'SEND Recruitment',
      category: 'city',
      services: ['Website', 'Social', 'Marketing'],
      image: '/images/clients/LTS Background - City.jpg',
      logo: '/images/clients/LTS Group Logo.png',
      logoSize: 'large',
    },
    // Sport - New order: Callum, Lawrence, Joe, Johnny
    {
      id: 7,
      title: 'Callum Ilott',
      subtitle: 'F1 Racing Driver',
      category: 'sport',
      services: ['Website Development'],
      image: '/images/clients/Callum Ilott - Sport.jpg',
      link: 'https://callumilott.com/',
    },
    {
      id: 8,
      title: 'Lawrence Okolie',
      subtitle: 'World Champion Boxer',
      category: 'sport',
      services: ['Brand Development'],
      image: '/images/clients/laurence okolie world title.jpg',
    },
    {
      id: 9,
      title: 'Joe Joyce',
      subtitle: 'Heavyweight Boxer',
      category: 'sport',
      services: ['Social Media', 'Video Production', 'Website', 'Merchandise'],
      image: '/images/clients/Joe Joyce - sport.jpg',
      link: 'https://joejoyceboxing.com/',
    },
    {
      id: 10,
      title: 'Johnny Fisher',
      subtitle: 'Heavyweight Boxer',
      category: 'sport',
      services: ['Merchandise'],
      image: '/images/clients/Johnny fisher - Sport.jpg',
    },
    // Music - Stiles & Drewe first, Kerry Ellis, NO Aled Jones
    {
      id: 11,
      title: 'Stiles & Drewe',
      subtitle: 'British Theatre Composers',
      category: 'music',
      services: ['Website', 'Store', 'Imagery'],
      image: '/images/clients/stilesdrewe.jpg',
    },
    {
      id: 12,
      title: 'Kerry Ellis',
      subtitle: 'West End Star',
      category: 'music',
      services: ['Website', 'Branding'],
      image: '/images/clients/Kerry Ellis.jpg',
    },
    {
      id: 13,
      title: 'Mark Kingswood',
      subtitle: 'Music Artist',
      category: 'music',
      services: ['Website', 'Music Videos'],
      image: '/images/clients/Mark Kingswood - Music.jpg',
    },
    // Entertainment
    {
      id: 14,
      title: 'Teletubbies',
      subtitle: '1st Ever Theatre Tour',
      category: 'entertainment',
      services: ['Campaign', 'Marketing'],
      image: '/images/clients/Teletubbies Entertainment.jpg',
    },
    {
      id: 15,
      title: 'Tom Gates',
      subtitle: 'National Tour',
      category: 'entertainment',
      services: ['Website'],
      image: '/images/clients/Brilliant World Of Tom Gates - Entertainment.jpg',
    },
    {
      id: 16,
      title: 'Fat Friends',
      subtitle: 'UK Tour',
      category: 'entertainment',
      services: ['Website'],
      image: '/images/clients/Fat Friends The Musical - Entertainment.jpg',
    },
    {
      id: 17,
      title: 'Fireman Sam',
      subtitle: 'National Tour',
      category: 'entertainment',
      services: ['Website'],
      image: '/images/clients/Fireman Sam - entertainment.jpg',
    },
    {
      id: 18,
      title: 'Doctor Dolittle',
      subtitle: 'Theatre Production',
      category: 'entertainment',
      services: ['Campaign'],
      image: '/images/clients/Doctor Doolittle - entertainment.jpg',
    },
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  return (
    <div data-testid="work-page">
      {/* Hero */}
      <section className="min-h-[50vh] flex items-center relative overflow-hidden bg-elate-black">
        <div className="container-custom relative z-10 pt-32 pb-20">
          <motion.p
            className="caption text-elate-yellow mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Our Work
          </motion.p>

          <motion.h1
            className="text-headline text-white mb-8"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Case
            <br />
            <span className="text-elate-yellow">Studies</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Brands we've empowered across city, sport, music, and entertainment. From award-winning law firms to boxing world champions.
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-elate-surface border-b border-white/10">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-2 uppercase tracking-wider text-sm font-semibold transition-colors ${
                  activeFilter === filter
                    ? 'bg-elate-yellow text-elate-black'
                    : 'bg-transparent text-gray-400 hover:text-white border border-white/20'
                }`}
                data-testid={`filter-${filter}`}
              >
                {filter === 'all' ? 'All Projects' : `#${filter}`}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-elate-surface">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                data-testid={`project-${project.id}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden mb-6">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-elate-black/90 via-elate-black/30 to-transparent" />
                  
                  {/* Custom colour gradient overlay for specific clients */}
                  {project.gradient && (
                    <div className={`absolute inset-0 ${project.gradientDirection} ${project.gradient}`} />
                  )}
                  
                  {/* Colour logo overlay for city clients */}
                  {project.logo && (
                    <div className="absolute top-6 left-6 right-6">
                      <img
                        src={project.logo}
                        alt={`${project.title} logo`}
                        className={`w-auto object-contain drop-shadow-lg ${
                          project.logoSize === 'xlarge' ? 'h-16' : 
                          project.logoSize === 'large' ? 'h-14' : 'h-10'
                        }`}
                      />
                    </div>
                  )}}
                  
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-4 right-4 bg-elate-yellow text-elate-black p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
                    >
                      <ExternalLink size={20} />
                    </a>
                  )}
                  
                  {/* Content at bottom with hashtag */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="inline-block px-2 py-1 bg-black/60 text-elate-yellow text-xs uppercase tracking-wider mb-2">#{project.category}</p>
                    <h3 className="text-2xl font-heading uppercase text-white mb-1 group-hover:text-elate-yellow transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-400 text-sm">{project.subtitle}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {project.services.map((service) => (
                    <span
                      key={service}
                      className="text-xs uppercase tracking-wider text-gray-500 border border-gray-700 px-2 py-1"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-elate-black">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-subhead text-white mb-6">
              Want to Be Next?
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-8 text-lg">
              We're always looking for exciting projects. Let's discuss how we can empower your brand with AI-driven strategy and creative.
            </p>
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
            >
              Start a Project
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WorkPage;
